import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Loader2, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/useDebounce';
import {
  useReceivingOrder,
  useCreateReceivingOrder,
  useUpdateReceivingOrder,
  useStartReceiving,
} from '../../hooks/useWmsReceiving';
import { useSupplier } from '../../hooks/useWmsSuppliers';
import { useCatalogSkuSearch } from '../../hooks/useWmsSkus';
import { ReceivingStatusBadge } from './ReceivingStatusBadge';
import { SupplierInfiniteCombobox } from '../suppliers/SupplierInfiniteCombobox';
import type { SkuSearchResult, UpdateReceivingOrderInput } from '../../types';

interface ReceivingDetailPanelProps {
  orderId: string | null;
  mode: 'view' | 'create';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (id: string) => void;
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground uppercase tracking-wide">{label}</Label>
      {children}
    </div>
  );
}

export function ReceivingDetailPanel({
  orderId,
  mode,
  open,
  onOpenChange,
  onCreated,
}: ReceivingDetailPanelProps) {
  const { t } = useTranslation('common');
  const isCreate = mode === 'create';
  const orderQuery = useReceivingOrder(isCreate ? '' : orderId ?? '');
  const createMutation = useCreateReceivingOrder();
  const updateMutation = useUpdateReceivingOrder();
  const startMutation = useStartReceiving();

  const order = orderQuery.data;

  const [poNumber, setPoNumber] = useState('');
  const [supplierId, setSupplierId] = useState<string | undefined>();
  const [supplierName, setSupplierName] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [receivedDate, setReceivedDate] = useState('');
  const [notes, setNotes] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrierSupplierId, setCarrierSupplierId] = useState<string | undefined>();
  const [carrier, setCarrier] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [supplierOrderNumber, setSupplierOrderNumber] = useState('');
  const [description, setDescription] = useState('');
  const [purchaseUrl, setPurchaseUrl] = useState('');

  // Resolve current supplier name from API if needed (for display in fallback)
  const supplierLookup = useSupplier(supplierId);

  useEffect(() => {
    if (isCreate) {
      setPoNumber(''); setSupplierId(undefined); setSupplierName('');
      setExpectedDate(''); setReceivedDate(''); setNotes(''); setTrackingNumber('');
      setCarrierSupplierId(undefined); setCarrier('');
      setPaymentMethod(''); setSupplierOrderNumber('');
      setDescription(''); setPurchaseUrl('');
      return;
    }
    if (order) {
      setPoNumber(order.po_number ?? '');
      setSupplierId(order.supplier_id ?? undefined);
      setSupplierName(order.supplier_name ?? '');
      setExpectedDate(order.expected_date?.slice(0, 10) ?? '');
      setReceivedDate(order.received_date?.slice(0, 10) ?? '');
      setNotes(order.notes ?? '');
      setTrackingNumber(order.tracking_number ?? '');
      setCarrierSupplierId(order.carrier_supplier_id ?? undefined);
      setCarrier(order.carrier ?? '');
      setPaymentMethod(order.payment_method ?? '');
      setSupplierOrderNumber(order.supplier_order_number ?? '');
      setDescription(order.description ?? '');
      setPurchaseUrl(order.purchase_url ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id, isCreate]);

  const save = (data: UpdateReceivingOrderInput) => {
    if (!order?.id || isCreate) return;
    updateMutation.mutate({ id: order.id, data });
  };

  // Description SKU autocomplete
  const [descSuggestOpen, setDescSuggestOpen] = useState(false);
  const [descHighlight, setDescHighlight] = useState(0);
  const descContainerRef = useRef<HTMLDivElement>(null);
  const descInputRef = useRef<HTMLInputElement>(null);
  const debouncedDescription = useDebounce(description, 200);
  const descSearchEnabled = descSuggestOpen && debouncedDescription.trim().length >= 1;
  const { data: descSkuResults, isLoading: descSkusLoading } = useCatalogSkuSearch(
    debouncedDescription || undefined,
    10,
    descSearchEnabled,
  );

  useEffect(() => {
    if (!descSuggestOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (descContainerRef.current && !descContainerRef.current.contains(e.target as Node)) {
        setDescSuggestOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [descSuggestOpen]);

  const pickDescriptionSku = (sku: SkuSearchResult) => {
    const next = sku.nameEn || sku.nameFr || sku.sku;
    setDescription(next);
    setDescSuggestOpen(false);
    if (!isCreate && order && next !== (order.description ?? '')) {
      save({ description: next });
    }
  };

  const onDescriptionKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!descSuggestOpen || !descSkuResults || descSkuResults.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setDescHighlight((i) => Math.min(i + 1, descSkuResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setDescHighlight((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      const pick = descSkuResults[descHighlight];
      if (pick) {
        e.preventDefault();
        pickDescriptionSku(pick);
      }
    } else if (e.key === 'Escape') {
      setDescSuggestOpen(false);
    }
  };

  const handleCreate = async () => {
    const created = await createMutation.mutateAsync({
      po_number: poNumber.trim() || undefined,
      supplier_id: supplierId && supplierId !== 'all' ? supplierId : undefined,
      supplier_name: supplierName.trim() || undefined,
      expected_date: expectedDate || undefined,
      received_date: receivedDate || undefined,
      notes: notes.trim() || undefined,
      tracking_number: trackingNumber.trim() || undefined,
      carrier_supplier_id: carrierSupplierId && carrierSupplierId !== 'all' ? carrierSupplierId : undefined,
      carrier: carrier.trim() || undefined,
      payment_method: paymentMethod.trim() || undefined,
      supplier_order_number: supplierOrderNumber.trim() || undefined,
      description: description.trim() || undefined,
      purchase_url: purchaseUrl.trim() || undefined,
    });
    onCreated?.(created.id);
  };

  if (!open) return null;

  return (
    <div className="h-full flex flex-col border-l bg-background overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b shrink-0 gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {!isCreate && order && (
            <ReceivingStatusBadge status={order.status} />
          )}
          <h2 className="text-sm font-mono font-semibold truncate">
            {isCreate ? 'New Procurement Order' : order?.order_number ?? 'Order'}
          </h2>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {!isCreate && order && (
            <Button asChild variant="outline" size="sm" className="h-7" title="Open full editor">
              <Link to={`/procurement/${order.id}`}>
                <ExternalLink className="h-3.5 w-3.5 mr-1" />
                Open
              </Link>
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => onOpenChange(false)}
            title="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        <Section label="PO Number">
          <Input
            className="text-sm font-mono"
            value={poNumber}
            onChange={(e) => setPoNumber(e.target.value)}
            onBlur={() => !isCreate && order && poNumber !== (order.po_number ?? '') && save({ po_number: poNumber.trim() || undefined })}
            placeholder="PO-12345"
            autoFocus={isCreate}
          />
        </Section>

        <Section label="Supplier">
          <SupplierInfiniteCombobox
            value={supplierId ?? 'all'}
            onChange={(v) => {
              const next = v === 'all' ? undefined : v;
              setSupplierId(next);
              if (!isCreate && order && next !== order.supplier_id) {
                save({ supplier_id: next, supplier_name: supplierLookup.data?.name });
              }
            }}
            onSupplierSelected={(s) => setSupplierName(s?.name ?? '')}
            placeholder="No supplier"
            fallbackLabel={supplierName || undefined}
          />
        </Section>

        <Section label="Expected Date">
          <Input
            type="date"
            className="text-sm"
            value={expectedDate}
            onChange={(e) => setExpectedDate(e.target.value)}
            onBlur={() => !isCreate && order && expectedDate !== (order.expected_date?.slice(0, 10) ?? '') && save({ expected_date: expectedDate || undefined })}
          />
        </Section>

        <Section label="Description">
          <div className="relative" ref={descContainerRef}>
            <Input
              ref={descInputRef}
              className="text-sm"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setDescSuggestOpen(e.target.value.trim().length >= 1);
                setDescHighlight(0);
              }}
              onFocus={() => {
                if (description.trim().length >= 1) setDescSuggestOpen(true);
              }}
              onKeyDown={onDescriptionKeyDown}
              onBlur={() => !isCreate && order && description !== (order.description ?? '') && save({ description: description.trim() || undefined })}
              placeholder="What was ordered (e.g. 1000 Transmitter PCB)"
              autoComplete="off"
            />
            {descSuggestOpen && (
              <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-auto rounded-md border bg-popover shadow-md">
                {descSkusLoading ? (
                  <div className="flex items-center justify-center gap-2 px-3 py-3 text-xs text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    {t('procurement.descriptionSearching', 'Searching…')}
                  </div>
                ) : !descSkuResults || descSkuResults.length === 0 ? (
                  <div className="px-3 py-3 text-xs text-muted-foreground">
                    {t('procurement.descriptionNoMatch', 'No matching SKU — keep typing to use as custom description.')}
                  </div>
                ) : (
                  <ul role="listbox">
                    {descSkuResults.map((sku, idx) => (
                      <li
                        key={sku.id}
                        role="option"
                        aria-selected={idx === descHighlight}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          pickDescriptionSku(sku);
                        }}
                        onMouseEnter={() => setDescHighlight(idx)}
                        className={cn(
                          'flex cursor-pointer items-start gap-2 px-3 py-1.5 text-xs',
                          idx === descHighlight && 'bg-accent text-accent-foreground',
                        )}
                      >
                        <span className="font-mono font-medium shrink-0">{sku.sku}</span>
                        <span className="truncate text-muted-foreground">
                          {sku.nameEn || sku.nameFr}
                          {sku.manufacturerName ? ` · ${sku.manufacturerName}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </Section>

        <Section label="Received Date">
          <Input
            type="date"
            className="text-sm"
            value={receivedDate}
            onChange={(e) => setReceivedDate(e.target.value)}
            onBlur={() => !isCreate && order && receivedDate !== (order.received_date?.slice(0, 10) ?? '') && save({ received_date: receivedDate || undefined })}
          />
        </Section>

        <div className="grid grid-cols-2 gap-3">
          <Section label="Carrier">
            <SupplierInfiniteCombobox
              value={carrierSupplierId ?? 'all'}
              kind={undefined}
              tag="carrier"
              onChange={(v) => {
                const next = v === 'all' ? undefined : v;
                setCarrierSupplierId(next);
                if (!isCreate && order && next !== (order.carrier_supplier_id ?? undefined)) {
                  save({ carrier_supplier_id: next ?? null });
                }
              }}
              onSupplierSelected={(s) => {
                const name = s?.name ?? '';
                setCarrier(name);
                if (!isCreate && order && name !== (order.carrier ?? '')) {
                  save({ carrier: name || null });
                }
              }}
              placeholder="No carrier"
              fallbackLabel={carrier || undefined}
            />
          </Section>
          <Section label="Tracking Number">
            <Input
              className="text-sm font-mono"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              onBlur={() => !isCreate && order && trackingNumber !== (order.tracking_number ?? '') && save({ tracking_number: trackingNumber.trim() || undefined })}
              placeholder="Carrier tracking #"
            />
          </Section>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Section label="Supplier Order #">
            <Input
              className="text-sm font-mono"
              value={supplierOrderNumber}
              onChange={(e) => setSupplierOrderNumber(e.target.value)}
              onBlur={() => !isCreate && order && supplierOrderNumber !== (order.supplier_order_number ?? '') && save({ supplier_order_number: supplierOrderNumber.trim() || undefined })}
              placeholder="Mouser/Digikey #"
            />
          </Section>
          <Section label="Payment Method">
            <Input
              className="text-sm"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              onBlur={() => !isCreate && order && paymentMethod !== (order.payment_method ?? '') && save({ payment_method: paymentMethod.trim() || undefined })}
              placeholder="VISA CEO 7828"
            />
          </Section>
        </div>

        <Section label="Purchase URL">
          <Input
            className="text-sm"
            value={purchaseUrl}
            onChange={(e) => setPurchaseUrl(e.target.value)}
            onBlur={() => !isCreate && order && purchaseUrl !== (order.purchase_url ?? '') && save({ purchase_url: purchaseUrl.trim() || undefined })}
            placeholder="https://..."
          />
        </Section>

        {!isCreate && order?.requester_name && (
          <Section label="Requester">
            <p className="text-sm">{order.requester_name}</p>
          </Section>
        )}

        {!isCreate && order && order.total_cost ? (
          <Section label="Total Cost (computed from lines)">
            <p className="text-sm tabular-nums font-medium">
              {Number(order.total_cost).toLocaleString('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2 })}
            </p>
          </Section>
        ) : null}

        <Section label="Notes">
          <Textarea
            className="text-sm min-h-[160px]"
            rows={6}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => !isCreate && order && notes !== (order.notes ?? '') && save({ notes: notes.trim() || undefined })}
            placeholder="Internal notes, carrier, tracking, etc."
          />
        </Section>

        {!isCreate && order && order.status === 'draft' && (
          <Button
            variant="outline"
            className="w-full"
            disabled={startMutation.isPending}
            onClick={() => startMutation.mutate(order.id)}
          >
            Start Procurement
          </Button>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t shrink-0">
        {isCreate ? (
          <Button
            className="w-full"
            disabled={createMutation.isPending}
            onClick={handleCreate}
          >
            Create Order
          </Button>
        ) : order ? (
          <p className="text-xs text-muted-foreground">
            Created {new Date(order.created_at).toLocaleDateString()} by {order.created_by_name || order.created_by}
          </p>
        ) : null}
      </div>
    </div>
  );
}
